import * as React from 'react';
import { isValidListOfEmails } from 'app/infrastructure/validation/email';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import { ShareCaseArgs } from 'app/area/case/features/share-case/types';
import { Link } from 'react-router-dom';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { routeConstants } from 'app/routes/route-constants';
import { ComplexValue } from 'ui/common/types';
import { SelectMultiple } from 'app/area/shared/translated-components';
import { Textarea } from 'ui/textarea';
import { MultiEmails } from 'app/area/case/components/multi-emails';

export interface ShareCaseModalProps {
  translate?: {[key:string] : string};
  userEmail?: string;
  caseId?: number;
  onCancel?: () => void;
  onContinue?: () => void;
  getUsers: (caseNumber: number, query?: string) => CodetableWireFormat[];
  shareDocument: (args: Partial<ShareCaseArgs>, translations: {[key:string]:string}) => void;
  recipients?: ComplexValue<string>[];
  message?: string;
  locale: string;
}

export interface ShareCaseModalState {
  options?: ComplexValue<string>[];
  recipients: ComplexValue<string>[];
  message?: string;
  valid?: boolean;
  externalRecipients?: string[];
}

export class ShareCaseModal extends React.Component<ShareCaseModalProps, ShareCaseModalState> {

  public constructor(props: ShareCaseModalProps){
    super(props);
    this.state = {
      recipients: (props.recipients || []),
      message: props.message,
      externalRecipients: []
    };
  }

  public componentDidMount() {
    this.validate();
  }

  private loadRecipients = async ({query} : {query:string}) => {
    const users = await this.props.getUsers(this.props.caseId, query);
    this.setState({ options: users.map(codeItemToStoreValue) });
  };

  private handleEmailChanged = (values:any) => {
    const emails = values && values.map(v => v.value).join(',');
    const noSelectedValues = !values || !emails.length;
    if( isValidListOfEmails(emails) || noSelectedValues ){
      this.setState(
        {recipients: values}
      ,
        this.validate
      );
    }
  };

  private handleSave = async ()=> {
    const {valid, recipients: selectedUsers, message, externalRecipients} = this.state;
    const {shareDocument, onContinue, translate} = this.props;
    if(valid){
      const allRecipients = [...selectedUsers.map(r => r.value),...externalRecipients];
      //build unique array of recipients
      const recipients = allRecipients.filter((item,index) => allRecipients.indexOf(item) === index);
      shareDocument && shareDocument({recipients, message}, translate);
      onContinue && onContinue();
    }
  };

  private handleCancel = async () => {
    this.props.onCancel();
  };

  private validate = () => {
    const userEmailExists = !!this.props.userEmail;
    const atLeastOneRecipientSelected = this.state.recipients.filter(r => Boolean(r.value) ).length > 0;
    const atLeastOneExternalRecipientSelected = this.state.externalRecipients.length > 0;
    this.setState({
      valid: (userEmailExists && atLeastOneRecipientSelected) || atLeastOneExternalRecipientSelected
    });
  };

  private setMessage = (value:any) => {
    this.setState({
      message: value
    },
      this.validate
    );
  };

  private handleChangeExternalRecipients = (emails: string[]) => {
    this.setState({
      externalRecipients: emails },
        this.validate
      );
  };

  public render(){
    const {
      translate,
      userEmail,
      locale
    } = this.props;
    const {
      options,
      recipients,
      message,
      externalRecipients,
      valid
    } = this.state;

    const multiExternalRecipients =
      <div className={'form-group email-recipients'}>
        <MultiEmails
          componentId={'multiple-external-recipients'}
          className={'multiple-external-recipients'}
          emails={externalRecipients}
          onChange={this.handleChangeExternalRecipients}
          label={{text: translate.external_recipients, visible: true}}
        />
      </div>;

    return (
      <ModalWrapper
        headerTitle={translate.share_case_by_email}
        btnTitle={translate.send}
        btnCancelTitle={translate.cancel}
        onClose={this.handleCancel}
        onDone={this.handleSave}
        isDisabled={!valid}
        closeOnDone={false}
        canCloseOnBackground={false}>
        {
        !userEmail ?
          <div className="alert alert-error">
            <p>{translate.no_user_email_exists}</p>
            <Link to={routeConstants.userProfileMainView}>{translate.go_to_profile}</Link>
          </div>
        :
          <>
            <p>{translate.select_users_info}</p>
            <SelectMultiple
              focus={true}
              hideAlphabeticIndex={false}
              className={'email-recipients'}
              componentId={'email-recipients'}
              options={options}
              label={{text: translate.recipients, visible: true}}
              valueItems={recipients}
              onLoadOptions={this.loadRecipients}
              onChange={this.handleEmailChanged}
              locale={locale}
            />
            {multiExternalRecipients}
            <Textarea
              onChange={this.setMessage}
              maxLength={3900}
              value={message}
              label={ {text:translate.message_to_receiver, visible: true} }/>
          </>
        }
      </ModalWrapper>
    );
  }
}
